import { Controller } from "@hotwired/stimulus"

// In-lesson Poll runtime (TMS-809). Steps through the block's poll questions, posts the
// learner's answer to submit_poll, and reveals the peer results in place.
//
// In the course PREVIEW the poll is answerable but nothing is recorded (TMS-891): an
// author has to be able to see their poll behave before they publish it. The tally is
// built locally from the one vote just cast and handed to the SAME applyResults, so
// preview exercises the real render path rather than a lookalike. The server never
// hears about it — the view omits the submit URL entirely in preview, so the request
// is unconstructible rather than merely skipped.
//
// This is the only in-lesson block that persists anything, so it is also the only one
// that talks to the server: the reader sets data-turbo="false", so the answer goes out
// as a fetch with the CSRF meta token rather than a form post, and the results are
// revealed without a page load — a redirect would lose the learner's place mid-lesson.
//
// Every state is already in the DOM, server-rendered. This controller only toggles which
// one is visible and writes bar widths, percentages, the total and the "You" pill, so no
// markup is built here and server-stored option text is never re-injected.
export default class extends Controller {
  static targets = ["question", "progressLabel", "progressFill", "previousButton", "nextButton"]
  static values = {
    submitUrl: String,
    preview: Boolean
  }

  connect() {
    this.questionIndex = 0
    this.showQuestion(0)
  }

  select(event) {
    const question = event.currentTarget.closest("[data-poll-target='question']")
    const submit = question?.querySelector("[data-poll-submit]")
    if (!submit) return

    this.hideError(question)
    submit.disabled = false
  }

  async submit(event) {
    event.preventDefault()

    const question = event.currentTarget.closest("[data-poll-target='question']")
    const chosen = question?.querySelector(".academy-poll-option-input:checked")
    if (!question || !chosen) return

    const submit = question.querySelector("[data-poll-submit]")
    if (submit) submit.disabled = true
    this.hideError(question)

    // Checked BEFORE postAnswer rather than relying on it failing: with no submit URL
    // it returns null, which would surface "we couldn't record your answer" — a false
    // alarm for a preview that was never meant to record anything.
    const payload = this.previewValue
      ? this.previewPayload(question, chosen.value)
      : await this.postAnswer(question.dataset.pollKey, chosen.value)
    if (!payload || payload.error) {
      this.showError(question, payload?.error || "We couldn't record your answer. Please try again.")
      if (submit) submit.disabled = false
      return
    }

    this.applyResults(question, payload)
    this.revealResults(question)
  }

  // Returning to the ballot leaves the learner's answer selected, so submitting again
  // without changing anything is a no-op rather than a surprise. Submit comes back
  // enabled with it — a disabled button beside a visibly chosen option reads as broken.
  changeAnswer(event) {
    event.preventDefault()

    const question = event.currentTarget.closest("[data-poll-target='question']")
    if (!question) return

    this.setHidden(question, "[data-poll-results]", true)
    this.setHidden(question, "[data-poll-ballot]", false)

    const chosen = question.querySelector(".academy-poll-option-input:checked")
    const submit = question.querySelector("[data-poll-submit]")
    if (submit) submit.disabled = !chosen
    chosen?.focus()
  }

  previousQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.questionIndex - 1)
  }

  nextQuestion(event) {
    event.preventDefault()
    this.showQuestion(this.questionIndex + 1)
  }

  // The tally a preview shows: the reviewer's own vote and nothing else. Shaped exactly
  // like the server's response so applyResults cannot tell the two apart. Peer numbers
  // are not invented — one vote reads as one vote.
  previewPayload(question, chosenOptionKey) {
    const options = Array.from(question.querySelectorAll("[data-poll-bar]")).map((bar) => ({
      key: bar.dataset.optionKey,
      percentage: bar.dataset.optionKey === chosenOptionKey ? 100 : 0
    }))

    return { poll_key: question.dataset.pollKey, chosen_option_key: chosenOptionKey, total: 1, options: options }
  }

  async postAnswer(pollKey, optionKey) {
    if (!this.hasSubmitUrlValue) return null

    try {
      const response = await fetch(this.submitUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin",
        body: JSON.stringify({ poll_key: pollKey, option_key: optionKey })
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.success) return data

      // A refusal the learner can't retry past (viewing as another user) comes with its
      // reason; show that rather than the generic "try again".
      return response.status === 403 && data.errors?.length ? { error: data.errors[0] } : null
    } catch (error) {
      return null
    }
  }

  // Percentages are computed server-side (largest remainder, so they sum to 100); this
  // only paints them. With peer results off the bars stay empty and unlabelled — the
  // learner just sees their own choice held. A company tally still under its anonymity
  // floor (TMS-1064) arrives as `below_minimum` with no numbers at all, and is painted
  // the same way, with the "results will appear" note in place of the total.
  applyResults(question, payload) {
    const belowMinimum = payload.below_minimum === true
    const showResults = question.dataset.pollShowResults === "true" && !belowMinimum
    const byKey = new Map((payload.options || []).map((option) => [option.key, option]))

    const total = question.querySelector("[data-poll-total]")
    if (total) total.hidden = !showResults
    if (total && showResults) {
      total.textContent = `${payload.total} ${payload.total === 1 ? "Response" : "Responses"}`
    }
    this.setHidden(question, "[data-poll-below-minimum]", !belowMinimum)

    question.querySelectorAll("[data-poll-bar]").forEach((bar) => {
      const option = byKey.get(bar.dataset.optionKey)
      const mine = bar.dataset.optionKey === payload.chosen_option_key
      const fill = bar.querySelector("[data-poll-bar-fill]")
      const percentage = bar.querySelector("[data-poll-bar-percentage]")

      bar.classList.toggle("is-mine", mine)
      if (fill) fill.style.width = showResults && option ? `${option.percentage}%` : "0%"
      if (percentage) percentage.hidden = !showResults
      if (percentage && showResults && option) percentage.textContent = `${option.percentage}%`

      const you = bar.querySelector("[data-poll-bar-you]")
      if (you) you.hidden = !mine
    })
  }

  revealResults(question) {
    this.setHidden(question, "[data-poll-ballot]", true)
    this.setHidden(question, "[data-poll-results]", false)
  }

  showQuestion(index) {
    const questions = this.questionTargets
    if (questions.length === 0) return

    this.questionIndex = Math.min(Math.max(index, 0), questions.length - 1)
    questions.forEach((question, questionIndex) => {
      question.hidden = questionIndex !== this.questionIndex
    })

    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `Question ${this.questionIndex + 1} of ${questions.length}`
    }
    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${((this.questionIndex + 1) / questions.length) * 100}%`
    }
    if (this.hasPreviousButtonTarget) this.previousButtonTarget.disabled = this.questionIndex === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = this.questionIndex === questions.length - 1
  }

  showError(question, message) {
    const error = question.querySelector("[data-poll-error]")
    if (!error) return

    error.textContent = message
    error.hidden = false
  }

  hideError(question) {
    const error = question.querySelector("[data-poll-error]")
    if (!error) return

    error.textContent = ""
    error.hidden = true
  }

  setHidden(question, selector, hidden) {
    const element = question.querySelector(selector)
    if (element) element.hidden = hidden
  }
}
